import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription, filter } from 'rxjs';
import { TableLazyLoadEvent } from 'primeng/table';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { pageChange } from 'src/app/services/table';
import { selectDeletedExportDevice, selectExportDeviceList, selectExportDeviceListLoading, selectExportDevicePaging, selectExportedDevice, selectUpdatedExportDevice } from 'src/app/store/user-file/user-file.selector';
import { Tooltip } from 'primeng/tooltip';
import { selectBoardUsersTaskRunning, selectCurrentDeviceStatus } from 'src/app/store/device/device.selector';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { DeviceStatus } from 'src/app/model/device-status';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { ExportDeviceDialogComponent } from '../export-dialog/export-dialog.component';
import { ExportInfo } from 'src/app/model/export';
import { EXPORT_LIST_PAGE_DEFAULT_SIZE, EXPORT_LIST_PAGE_SIZE_OPTIONS } from 'src/app/conf/export';
import { ImportDeviceDialogComponent } from '../import-dialog/import-dialog.component';
import { selectActiveTask } from 'src/app/store/task/task.selector';
import { Task } from 'src/app/model/tasks';
import { selectIsImpersMode } from 'src/app/store/user/user.selector';

@Component({
	selector: 'app-import-export-list',
	templateUrl: './import-export-list.component.html',
	providers: []
})

export class ImportExportListComponent implements OnInit, OnDestroy {
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;
	isImpersMode: boolean;

	exportList: ExportInfo[];
	exportList$: Observable<ExportInfo[] | undefined>;
	exportListSub: Subscription | undefined;

	exportListLoading: boolean;
	exportListLoading$: Observable<boolean | undefined>;
	exportListLoadingSub: Subscription | undefined;

	exportListPaging$: Observable<ItemsPagination | undefined>;
	exportListPagingSub: Subscription | undefined;
	activeTasks$: Observable<Task[] | undefined>;
	activeTasksSub: Subscription | undefined;

	paging: PaginationParams = {
		size: EXPORT_LIST_PAGE_DEFAULT_SIZE,
		page: 1,
	};
	pageSizeOptions = EXPORT_LIST_PAGE_SIZE_OPTIONS;
	pageSize = EXPORT_LIST_PAGE_DEFAULT_SIZE;

	taskRunning: boolean;
	taskRunning$: Observable<boolean>;
	taskRunningSub: Subscription | undefined;

	offline: boolean;
	offline$: Observable<DeviceStatus>;
	offlineSub: Subscription | undefined;
	update$: Observable<boolean | undefined>;
	updateSub: Subscription;
	delete$: Observable<boolean | undefined>;
	deleteSub: Subscription;

	export$: Observable<boolean | undefined>;
	exportSub: Subscription;

	exportRun: boolean;
	opened = false;

	totalRecords = 0;

	sort: any;
	// paginator: any;

	// design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	COLUMN_TITLE_MIN_WD = 647;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	@ViewChild(Tooltip) tooltip!: Tooltip;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		this.exportRun = false;
		this.totalRecords = 0;
		this.taskRunning = false;
		this.offline = false;
		this.exportListLoading = true;
		this.exportList = [];
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		//this.getExportDeviceList();

		this.onResize();
	}

	initSelectors(): void {
		this.exportListLoading$ = this.store.select(selectExportDeviceListLoading);
		this.exportList$ = this.store.select(selectExportDeviceList);
		this.exportListPaging$ = this.store.select(selectExportDevicePaging);
		this.taskRunning$ = this.store.select(selectBoardUsersTaskRunning);
		this.offline$ = this.store.select(selectCurrentDeviceStatus);
		this.export$ = this.store.select(selectExportedDevice);
		this.update$ = this.store.select(selectUpdatedExportDevice);
		this.delete$ = this.store.select(selectDeletedExportDevice);
		this.activeTasks$ = this.store.select(selectActiveTask);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
	}

	showHideTooltip() {
		this.tooltip.activate();
	}

	getColumnTitle(): boolean {
		return (this.screenWidth <= this.COLUMN_TITLE_MIN_WD);
	}

	subscribeAll(): void {
		this.activeTasksSub = this.activeTasks$.subscribe(activeTasks => {
			if (activeTasks.filter(ts => ts.Type === 12).length > 0) {
				this.exportRun = true;
			} else if (this.exportRun) {
				this.exportRun = false;
				this.getExportDeviceList();
			}
		});
		this.exportListLoadingSub = this.exportListLoading$?.subscribe(
			(exportListLoading) => {
				this.exportListLoading = exportListLoading;
			}
		);
		this.exportListSub = this.exportList$?.subscribe(
			(exportList) => {
				this.exportList = exportList;
			}
		);
		this.exportListPagingSub = this.exportListPaging$?.pipe(filter(p => !!p))
			.subscribe(
				paging => {
					if (paging !== null && paging !== undefined) {
						this.totalRecords = paging.TotalCount;
					} else {
						this.totalRecords = 0;
					}
				}
			);
		this.offlineSub = this.offline$?.pipe(filter(d => !!d))
			.subscribe(
				(deviceStatus) => {
					this.offline = !deviceStatus.Online;
				}
			);
		this.taskRunningSub = this.taskRunning$.subscribe(
			(taskRunning) => {
				this.taskRunning = taskRunning;
			}
		);
		this.updateSub = this.update$?.subscribe(
			(res) => {
				if (res) {
					this.getExportDeviceList();
				}
			}
		);
		this.exportSub = this.export$?.pipe(filter(res => !!res)).subscribe(
			(res) => {
				if (res) {
					this.getExportDeviceList();
				}
			}
		);
		this.deleteSub = this.delete$?.subscribe(
			(res) => {
				if (res) {
					this.getExportDeviceList();
				}
			}
		);
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);

	}

	ngOnDestroy(): void {
		this.exportListLoadingSub?.unsubscribe();
		this.exportListSub?.unsubscribe();
		this.exportListPagingSub?.unsubscribe();
		this.offlineSub?.unsubscribe();
		this.taskRunningSub?.unsubscribe();
		this.updateSub?.unsubscribe();
		this.exportSub?.unsubscribe();
		this.deleteSub?.unsubscribe();
		this.activeTasksSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
	}

	getExportDeviceList(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(UserFileActions.getExportDeviceList({ paging: this.paging, isImpersMode: this.isImpersMode }));
		}
	}

	loadExportDeviceList(event: TableLazyLoadEvent) {
		this.paging = pageChange(this.paging, event);

		this.getExportDeviceList();
	}

	togglePanel(opened: boolean): void {
		this.opened = opened;
		this.getExportDeviceList();
	}

	isSelectTab(): boolean {
		return this.opened;
	}

	importList(exportInfo: ExportInfo): void {
		this.store.dispatch(UserFileActions.resetImportDeviceDialog());
		this.ref = this.dialogService.open(ImportDeviceDialogComponent, {
			header: this.translate.instant('IMPORT_LIST'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				exportInfo,
			}
		});
	}

	editList(exportInfo: ExportInfo): void {
		this.store.dispatch(UserFileActions.resetExportDeviceDialog());
		this.ref = this.dialogService.open(ExportDeviceDialogComponent, {
			header: this.translate.instant('EDIT_NAME') + ' ' + this.translate.instant('LIST').toLowerCase(),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				exportInfo,
			}
		});
	}

	deleteList(exportInfo: ExportInfo): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('EXPORT_DATA_DEVICE_DELETE_TITLE'),
			message: this.translate.instant('EXPORT_DATA_DEVICE_DELETE_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(UserFileActions.deleteExportDevice({
					exportInfo: exportInfo
				}));
			},
			reject: () => {
			}
		});
	}

	downloadUrl(url: string, fileName: string) {
		const a: any = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.style = 'display: none';
		a.click();
		a.remove();
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	onExtendedMobile(): boolean {
		return (this.screenWidth <= 660);
	}
}